import { useState, useMemo } from 'react';
import { useLiveDealersQuery } from './useDealersQuery';
import { useDebounce } from '@shared/hooks/useDebounce';
import type { Dealer } from '../types/dealers.types';

export interface DealerSearchOptions {
    /** Restricts the list to dealers the caller can sell this product for. */
    sellingProductId?: string;
}

export const useDealerSearchWithQuery = (options: DealerSearchOptions = {}) => {
    const { sellingProductId } = options;
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);

    // Debounce search term with 500ms delay
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // The term actually embedded in the current query key, so it always describes
    // the rows `dealers` holds. Empty-state decisions must use this and never the
    // raw input: for 500ms after a no-match search is cleared the raw term is
    // already empty while the rows are still the old search's.
    const appliedSearchTerm = useMemo(
        () => (debouncedSearchTerm.length >= 3 ? debouncedSearchTerm : ''),
        [debouncedSearchTerm]
    );

    // Request the backend `per_page` max so the embedded selector returns the
    // full dealer set in one page — there are no pagination controls in this
    // hook and the backend caps `per_page` at 50.
    const queryParams = useMemo(
        () => ({
            per_page: 50,
            sellable_only: true,
            context: 'sales' as const,
            ...(sellingProductId && { selling_product_id: sellingProductId }),
            ...(appliedSearchTerm && { search: appliedSearchTerm }),
        }),
        [appliedSearchTerm, sellingProductId]
    );

    const {
        data,
        isFetchedAfterMount,
        isError,
        error,
        refetch,
        isFetching,
    } = useLiveDealersQuery(queryParams);

    const freshData = isFetchedAfterMount && !isError ? data : undefined;
    const dealers = freshData?.data || ([] as Dealer[]);

    const selectDealer = (dealer: Dealer) => {
        setSelectedDealer(dealer);
    };

    const clearSelection = () => {
        setSelectedDealer(null);
    };

    const resetSearch = () => {
        setSearchTerm('');
        setSelectedDealer(null);
        refetch();
    };

    return {
        dealers,
        searchTerm,
        appliedSearchTerm,
        setSearchTerm,
        selectedDealer,
        selectDealer,
        clearSelection,
        resetSearch,
        isLoading: !isFetchedAfterMount || (isFetching && !freshData),
        isFetching,
        error,
        refetch,
        totalDealers: freshData?.meta?.total ?? (Array.isArray(dealers) ? dealers.length : 0),
        filteredCount: Array.isArray(dealers) ? dealers.length : 0,
    };
};
